/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

async function recoverPromiseRejection(
  event: PromiseRejectionEvent
): Promise<Error | string> {
  if (event.reason instanceof String || typeof event.reason === "string") {
    return event.reason.toString();
  }

  if (event.reason instanceof Error) {
    return event.reason;
  }

  if (event.reason) {
    return `Unhandled promise rejection with unusual reason: ${event.reason}`;
  }

  if (event.promise) {
    try {
      await event.promise;
    } catch (error) {
      return error;
    }
  }

  return `Unhandled promise rejection with no reason: ${event}`;
}

function isExpected(reason: Error | string | undefined): boolean {
  const message = reason instanceof Error ? reason.message : reason;
  return Boolean(/ResizeObserver/.test(message ?? ""));
}

export async function noticeUnhandledRejection(
  event: PromiseRejectionEvent
): Promise<void> {
  const reason = await recoverPromiseRejection(event);
  const stack = reason instanceof Error ? reason.stack : null;
  const expected = isExpected(reason);
  if (expected) {
    console.error(reason);
    event.preventDefault();
    event.stopPropagation();
  } else {
    const data: any = { stack };
    if (!(event instanceof PromiseRejectionEvent)) {
      // Need to adjust the type, because we know the event is not a PromiseRejectionEvent
      const eventAsAny = event as any;
      data.type = eventAsAny.type;
      data.detail = JSON.stringify(eventAsAny.detail);
      data.event = JSON.stringify(eventAsAny);
    }
    window.newrelic.noticeError(reason, data);
  }
}

export function registerUnhandledRejectionListeners(): void {
  addEventListener("unhandledrejection", noticeUnhandledRejection);
}
